// @ts-strict-ignore
import {
  Component,
  Input,
  ViewContainerRef,
  EventEmitter,
  Output,
  ViewChild,
  forwardRef,
  ElementRef,
  OnInit
} from '@angular/core';
import { LMFormPanelBase } from './lm-form-panel-base';
import { LMListTransactionEvent } from '@list/lm-list.component';
import { Observer, Observable, of } from 'rxjs';
import { LMPanelTransactionEvent } from '@form/services/lm-form-panel.service';
import { LMDashboardViewComponent } from '@dashboard/lm-dashboard-view.component';
import { LandmarkPanel } from './lm-form.model';
import { LMModel, LMModelField } from './model';
import { LMRefreshRequest, LMTwelveColumnGridComponent } from '@app/components';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { LMUrlUtilService } from '@app/services';

@Component({
  selector: 'lm-form-panel-dashboard',
  template: `
    <ng-container *ngIf="hasBackground; else noBackground"><section
        class="use-background-image background-image-attributes view-point-background"
        [style.background-image]="backgroundImageUrl">
      <ng-container [ngTemplateOutlet]="dashboardTemplate"></ng-container>
    </section>

    </ng-container>
    <ng-template #noBackground>
      <ng-container [ngTemplateOutlet]="dashboardTemplate"></ng-container>
    </ng-template>

    <ng-template #dashboardTemplate>
      <ng-container *ngIf="panel?.useTwelveColumnGrid; else defaultDashboard">
          <lm-twelve-column-grid
              *ngIf="activated"
              [formUrlInfo]="formUrlInfo"
              [dashboardPanels]="panels"
              [model]="model"
              [specFields]="specFields"
              [placeHolder]="placeHolder"
              [fromDataView]="fromDataView"
              [panelVisibilityUpdateTimeStamp]="panelVisibilityUpdateTimeStamp"
              (contentModified)="onContentModified($event)"
              (panelTransactionEvent)="onPanelTransactionEvent($event)"
              (drillAroundEvent)="onDrillAroundEvent($event)"
              (fieldEvent)="onFieldEvent($event)"
          ></lm-twelve-column-grid>
      </ng-container>
      <ng-template #defaultDashboard>
        <lm-dashboard-view
          *ngIf="activated"
          [formUrlInfo]="formUrlInfo"
          [dashboardPanels]="panels"
          [model]="model"
          [columns]="columns"
          [specFields]="specFields"
          [placeHolder]="placeHolder"
          [fromDataView]="fromDataView"
          [panelVisibilityUpdateTimeStamp]="panelVisibilityUpdateTimeStamp"
          (contentModified)="onContentModified($event)"
          (panelTransactionEvent)="onPanelTransactionEvent($event)"
          (drillAroundEvent)="onDrillAroundEvent($event)"
          (fieldEvent)="onFieldEvent($event)"
        ></lm-dashboard-view>
      </ng-template>
    </ng-template>
  `,
  // styles: [ `:host { height: 100%; display: block }` ]
})
export class LMFormPanelDashboardComponent implements LMFormPanelBase, OnInit {
  @ViewChild(forwardRef(() => LMDashboardViewComponent)) lmDashboardViewComponent; // : LMDashboardViewComponent;
  @ViewChild(forwardRef(() => LMTwelveColumnGridComponent)) lmTwelveColumnGridComponent;

  @Input() formUrlInfo: LMFormUrlInfo;
  @Input() panels: Array<LandmarkPanel>;
  @Input() panel: LandmarkPanel;
  @Input() placeHolder: ViewContainerRef;
  @Input() needsReload: boolean;
  @Input() panelDataState: LMPanelDataState;
  @Input() activated: boolean;
  @Input() panelId: string;
  @Input() model: LMModel;
  @Input() specFields: LMSpecFields;
  @Input() columns: 3 | 4 = 3;
  @Input() panelVisibilityUpdateTimeStamp: number;

  @Input() set fromDataView(fromDataView: LMDataView) {
    this._fromDataView = fromDataView;
  }

  get fromDataView(): LMDataView {
    if (this.needsReload || (this.panelDataState === 'stale_needsReload')) {

      // -------------------------------------------------------------
      // return null fromDataView so tabs non activated tabs will
      // not load their data data.
      // -------------------------------------------------------------
      return null;
    }

    return this._fromDataView;
  }

  @Output() formPanelContentLoaded = new EventEmitter<any>();
  @Output() contentModified = new EventEmitter<any>();
  @Output() updateToolbarStateEvent = new EventEmitter<any>();
  @Output() sendContextMessageEvent = new EventEmitter<any>();
  @Output() specialActionToolbarButtonEvent = new EventEmitter<LMSpecialActionToolbarButtonEvent>();
  @Output() panelTransactionEvent = new EventEmitter<LMPanelTransactionEvent>();
  @Output() fieldEvent = new EventEmitter<LMFieldEvent>();
  @Output() drillAroundEvent = new EventEmitter<LMDrillAroundEvent>();

  public hasBackground = false;
  public backgroundImageUrl: SafeStyle;

  private _fromDataView: LMDataView = null;

  constructor(
    private elementRef: ElementRef,
    private lmUrlUtilService: LMUrlUtilService,
    private _sanitizer: DomSanitizer
  ) { }

  ngOnInit() {
    if (this.panel) {
      this.hasBackground = !!this.panel.background;
      if (this.hasBackground) {
        this.buildBackgroundImageString();
      }
    }
  }

  refresh(request?: LMRefreshRequest): Observable<boolean> {
    if (this.lmTwelveColumnGridComponent) {
      return this.lmTwelveColumnGridComponent.refresh(request);
    } else if (this.lmDashboardViewComponent) {
      return this.lmDashboardViewComponent.refresh(request);
    } else {
      return of(true);
    }
  }

  public save(callBack: Observer<boolean>) {
    if (this.lmTwelveColumnGridComponent && this.isDirty()) {
      this.lmTwelveColumnGridComponent.runSaveAction();
    } else if (this.lmDashboardViewComponent && this.isDirty()) {
      this.lmDashboardViewComponent.runSaveAction();
    } else {
      callBack.next(true);
      callBack.complete();
    }
  }

  public isDirty(): boolean {
    if (this.lmTwelveColumnGridComponent) {
      return this.lmTwelveColumnGridComponent.isModified();
    } else if (this.lmDashboardViewComponent) {
      return this.lmDashboardViewComponent.isModified();
    } else {
      return false;
    }
  }

  /**
   * forces the panels to relayout. useful when switching tabs after having resized the browser.
   */
  public refreshPanels() {
    if (this.lmDashboardViewComponent) {
      this.lmDashboardViewComponent.refreshPanels(); // TODO thnking the method name should be more desciptive like layoutPanels
    }
  }

  public getPanelHeight(): number {
    if (this.elementRef) {
      return this.elementRef.nativeElement.offsetHeight;
    }
    return 0;
  }

  onContentModified(event: any) {
    this.contentModified.emit(event);
  }

  onSendContextMessageEvent(event: any) {
    this.sendContextMessageEvent.emit(event);
  }

  onPanelTransactionEvent(event: LMListTransactionEvent) {
    const panelTransactionEvt = new LMPanelTransactionEvent(this.panelId);
    const originatingList = event.eventSource ? event.eventSource.listId : undefined;
    panelTransactionEvt.originatingList = originatingList;
    this.panelTransactionEvent.emit(panelTransactionEvt);
  }

  onDrillAroundEvent(event: LMDrillAroundEvent) {
    this.drillAroundEvent.emit(event);
  }

  onFieldEvent(event: LMFieldEvent) {
    this.fieldEvent.emit(event);
  }

  saveCompositeComponentState(): void {
    if (this.lmTwelveColumnGridComponent) {
      this.lmTwelveColumnGridComponent.saveDashboardViewState();
    } else if (this.lmDashboardViewComponent) {
      this.lmDashboardViewComponent.saveDashboardViewState();
    }
  }

  clearCompositeComponentState(): void {
    if (this.lmTwelveColumnGridComponent) {
      this.lmTwelveColumnGridComponent.clearDashboardViewState();
    } else if (this.lmDashboardViewComponent) {
      this.lmDashboardViewComponent.clearDashboardViewState();
    }
  }

  private buildBackgroundImageString(): void {
    const relatedImage = !!this.panel.background.imageFieldId;

    if (relatedImage) {
      const imageField: LMModelField = this.model.getField(this.panel.background.imageFieldId);
      if (!imageField) {
        return;
      }

      const imageUrl: string = imageField.value;
      if (!imageUrl) {
        return;
      }

      const imageUrl2: string = this.lmUrlUtilService.removeParam(imageUrl, 'size');
      this.backgroundImageUrl = this._sanitizer.bypassSecurityTrustStyle(`url('${imageUrl2}')`);
    } else {
      this.backgroundImageUrl = this._sanitizer.bypassSecurityTrustStyle(`url('${this.panel.background.image}')`);
    }
  }
}
